import { UnreachableCaseError } from 'ts-essentials';
import { point3DToVector3, vector3ToPoint3D } from '@trimble/utilities';
import {
  isAlignmentMeasurement,
  type AlignmentMeasurementType,
  type Measurement,
} from '@trimble/web3d-providers';
import type {
  ReservedAlignmentMeasurement,
  ReservedMeasurement,
} from './multipane-view.types';

function toReservedAlignmentMeasurement(
  measurement: AlignmentMeasurementType
): ReservedAlignmentMeasurement {
  return {
    type: 'alignment',
    id: measurement.id,
    value: measurement.value,
    ground: vector3ToPoint3D(measurement.ground),
    point: vector3ToPoint3D(measurement.point),
    alignment: vector3ToPoint3D(measurement.alignment),
    ...(measurement.label !== undefined && { label: measurement.label }),
    ...(measurement.color && { color: { ...measurement.color } }),
    ...(measurement.hidden && { hidden: true }),
  };
}

/**
 * Serialize the measurement types that Connect Views cannot persist natively
 * (currently only alignment) for storage in reserved view data.
 */
export function toReservedMeasurements(
  measurements: readonly Measurement[]
): ReservedMeasurement[] {
  return measurements
    .filter(isAlignmentMeasurement)
    .map(toReservedAlignmentMeasurement);
}

function fromReservedAlignmentMeasurement(
  reserved: ReservedAlignmentMeasurement
): AlignmentMeasurementType {
  return {
    id: reserved.id,
    markups: [],
    type: 'alignment',
    value: reserved.value,
    ground: point3DToVector3(reserved.ground),
    point: point3DToVector3(reserved.point),
    alignment: point3DToVector3(reserved.alignment),
    ...(reserved.label !== undefined && { label: reserved.label }),
    ...(reserved.color && { color: { ...reserved.color } }),
    ...(reserved.hidden && { hidden: true }),
  };
}

/** Rebuild in-memory measurements from reserved view data for restoration. */
export function fromReservedMeasurements(
  reserved: readonly ReservedMeasurement[]
): Measurement[] {
  return reserved.map((measurement) => {
    const { type } = measurement;
    if (type === 'alignment') {
      return fromReservedAlignmentMeasurement(measurement);
    }
    throw new UnreachableCaseError(type);
  });
}
